import { useState, useEffect } from 'react';
import { Palette, Sliders } from 'lucide-react';

const SOLID_PRESETS = [
  { name: 'Azul', v: '#0147a0' },
  { name: 'Esmeralda', v: '#10b981' },
  { name: 'Roxo', v: '#7c3aed' },
  { name: 'Âmbar', v: '#f59e0b' },
  { name: 'Rosa', v: '#ec4899' },
  { name: 'Slate', v: '#0f172a' },
  { name: 'Branco', v: '#ffffff' },
  { name: 'Vermelho', v: '#d72d21' },
];

const GRAD_PRESETS = [
  { name: 'Aurora', v: 'linear-gradient(135deg, #0147a0, #06b6d4)' },
  { name: 'Sunset', v: 'linear-gradient(135deg, #f59e0b, #ec4899)' },
  { name: 'Forest', v: 'linear-gradient(135deg, #064e3b, #10b981)' },
  { name: 'Royal', v: 'linear-gradient(135deg, #7c3aed, #ec4899)' },
  { name: 'Slate', v: 'linear-gradient(180deg, #0f172a, #334155)' },
  { name: 'Ocean', v: 'linear-gradient(135deg, #001e45, #0147a0)' },
];

function isGrad(v) { return typeof v === 'string' && v.includes('gradient'); }

function parseGrad(v) {
  const m = v?.match(/linear-gradient\((\d+)deg,\s*(#[0-9a-fA-F]{3,8}),\s*(#[0-9a-fA-F]{3,8})\)/);
  return m ? { angle: parseInt(m[1]), c1: m[2], c2: m[3] } : { angle: 135, c1: '#0147a0', c2: '#06b6d4' };
}

function buildGrad(angle, c1, c2) { return `linear-gradient(${angle}deg, ${c1}, ${c2})`; }

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400";

export default function ColorPicker({ label, value = '#0147a0', onChange }) {
  const [mode, setMode] = useState(isGrad(value) ? 'gradient' : 'solid');
  const g = parseGrad(value);
  const [solid, setSolid] = useState(isGrad(value) ? '#0147a0' : value);
  const [c1, setC1] = useState(g.c1);
  const [c2, setC2] = useState(g.c2);
  const [angle, setAngle] = useState(g.angle);

  useEffect(() => {
    if (mode === 'solid') onChange(solid);
    else onChange(buildGrad(angle, c1, c2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, solid, c1, c2, angle]);

  const preview = mode === 'solid' ? solid : buildGrad(angle, c1, c2);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
          {['solid', 'gradient'].map(m => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={`px-2.5 py-0.5 text-[11px] font-bold rounded-md transition-colors ${mode === m ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
              {m === 'solid' ? 'Sólida' : 'Gradiente'}
            </button>
          ))}
        </div>
      </div>

      {/* Preview bar */}
      <div className="w-full h-9 rounded-xl border border-slate-200" style={{ background: preview }} />

      {mode === 'solid' ? (
        <div className="flex gap-2 items-center">
          <input type="color" value={solid} onChange={e => setSolid(e.target.value)}
            className="h-9 w-12 rounded-lg border border-slate-200 cursor-pointer flex-shrink-0" />
          <input type="text" value={solid} onChange={e => setSolid(e.target.value)} className={inp} />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex gap-2 items-center">
              <input type="color" value={c1} onChange={e => setC1(e.target.value)}
                className="h-9 w-10 rounded-lg border border-slate-200 cursor-pointer flex-shrink-0" />
              <input type="text" value={c1} onChange={e => setC1(e.target.value)}
                className={inp + ' px-2 py-1.5 text-xs'} placeholder="Cor 1" />
            </div>
            <div className="flex gap-2 items-center">
              <input type="color" value={c2} onChange={e => setC2(e.target.value)}
                className="h-9 w-10 rounded-lg border border-slate-200 cursor-pointer flex-shrink-0" />
              <input type="text" value={c2} onChange={e => setC2(e.target.value)}
                className={inp + ' px-2 py-1.5 text-xs'} placeholder="Cor 2" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Sliders className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-500 w-8">{angle}°</span>
            <input type="range" min="0" max="360" step="1" value={angle} onChange={e => setAngle(parseInt(e.target.value))} className="flex-1" />
            <div className="flex gap-1">
              {[0, 90, 135, 180].map(a => (
                <button key={a} type="button" onClick={() => setAngle(a)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 font-mono">{a}°</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Presets */}
      <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
        <Palette className="w-3 h-3 text-slate-400" />
        {SOLID_PRESETS.map(p => (
          <button key={p.name} type="button" title={p.name}
            onClick={() => { setMode('solid'); setSolid(p.v); }}
            className="w-5 h-5 rounded border border-slate-300 hover:scale-110 transition-transform flex-shrink-0"
            style={{ background: p.v }} />
        ))}
        <span className="text-slate-200 text-xs">|</span>
        {GRAD_PRESETS.map(p => (
          <button key={p.name} type="button" title={p.name}
            onClick={() => { setMode('gradient'); const g = parseGrad(p.v); setC1(g.c1); setC2(g.c2); setAngle(g.angle); onChange(p.v); }}
            className="w-7 h-5 rounded border border-slate-300 hover:scale-110 transition-transform flex-shrink-0"
            style={{ background: p.v }} />
        ))}
      </div>
    </div>
  );
}
